import type {
  Flow_AbstractComponent,
  Flow_Diff,
  Flow_ComponentProps,
  Flow_ObjMap,
} from 'src/types/flow-to-typescript-codemod';
import * as React from 'react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
} from '@spaced-out/ui-design-system/lib/floating-ui';

import {
  spaceNone,
  spaceXXSmall,
} from '@spaced-out/ui-design-system/lib/styles/variables/_space';

import {classify} from 'src/utils/classify';

import type {ButtonProps} from '@spaced-out/ui-design-system/lib/components/Button';
import type {
  MenuOption,
  MenuProps,
} from '@spaced-out/ui-design-system/lib/components/Menu';
import type {BaseTooltipProps} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {UnstyledButton} from '@spaced-out/ui-design-system/lib/components/Button';
import {ConditionalWrapper} from '@spaced-out/ui-design-system/lib/components/ConditionalWrapper';
import {Menu} from '@spaced-out/ui-design-system/lib/components/Menu';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';

import css from './custom-dropdown.css';


export const ANCHOR_POSITION_TYPE = Object.freeze({
  top: 'top',
  topStart: 'top-start',
  topEnd: 'top-end',
  bottom: 'bottom',
  bottomStart: 'bottom-start',
  bottomEnd: 'bottom-end',
  right: 'right',
  rightStart: 'right-start',
  rightEnd: 'right-end',
});

export const STRATEGY_TYPE = Object.freeze({
  absolute: 'absolute',
  fixed: 'fixed',
});

export type AnchorType =
  typeof ANCHOR_POSITION_TYPE[keyof typeof ANCHOR_POSITION_TYPE];
export type Strategy = typeof STRATEGY_TYPE[keyof typeof STRATEGY_TYPE];

type ClassNames = Readonly<{
  wrapper?: string;
  buttonWrapper?: string;
  dropdownContainer?: string;
  buttonIcon?: string;
}>;

export type CustomDropdownProps = ButtonProps & {
  classNames?: ClassNames;
  menu?: MenuProps;
  positionStrategy?: Strategy;
  anchorPosition?: AnchorType;
  onOptionSelect?: (
    option: MenuOption,
    arg2?: React.SyntheticEvent<HTMLElement> | null | undefined,
  ) => unknown;
  onMenuOpen?: () => unknown;
  onMenuClose?: () => unknown;
  tooltip?: BaseTooltipProps;
};

// @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<HTMLDivElement & RefAttributes<CustomDropdownProps>>' is not assignable to type 'AbstractComponent<CustomDropdownProps, HTMLDivElement>'.
export const CustomDropdown: Flow_AbstractComponent<
  CustomDropdownProps,
  HTMLDivElement
> = React.forwardRef<CustomDropdownProps, HTMLDivElement>(
  // @ts-expect-error - TS2345 - Argument of type '({ anchorPosition, positionStrategy, size, onOptionSelect, menu, classNames, disabled, onMenuOpen, onMenuClose, children, iconRightName, isFluid, tooltip, onClick, tabIndex, ...restButtonProps }: CustomDropdownProps, forwardRef: ForwardedRef<CustomDropdownProps>) => Element' is not assignable to parameter of type 'ForwardRefRenderFunction<CustomDropdownProps, HTMLDivElement>'.
  (
    {
      anchorPosition = 'bottom-start',
      positionStrategy = STRATEGY_TYPE.fixed,
      size = 'medium',
      onOptionSelect,
      menu,
      classNames,
      disabled,
      onMenuOpen,
      onMenuClose,
      children,
      iconRightName,
      isFluid,
      tooltip,
      onClick,
      tabIndex = -1,
      ...restButtonProps
    }: CustomDropdownProps,
    forwardRef,
  ) => {
    const menuBtnRef = React.useRef(null);
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'CustomDropdownProps'.
    React.useImperativeHandle(forwardRef, () => menuBtnRef.current);
    const {x, y, refs, strategy, context} = useFloating({
      open: isMenuOpen,
      onOpenChange: (open) => {
        setIsMenuOpen(open);
        open ? onMenuOpen?.() : onMenuClose?.();
      },
      placement: anchorPosition,
      strategy: positionStrategy,
      middleware: [offset(parseInt(spaceXXSmall)), flip(), shift()],
      whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);

    const {getReferenceProps, getFloatingProps} = useInteractions([
      click,
      dismiss,
      role,
    ]);

    return (
      <div
        data-testid="ButtonDropdown"
        className={classify(
          css.customDropdownContainer,
          {[css.isFluid]: isFluid === true},
          classNames?.dropdownContainer,
        )}
        ref={menuBtnRef}
      >
        <ConditionalWrapper
          condition={Boolean(tooltip)}
          wrapper={(children) => (
            <Tooltip {...tooltip} hidden={isMenuOpen ? true : tooltip?.hidden}>
              {children}
            </Tooltip>
          )}
        >
          <UnstyledButton
            {...getReferenceProps({
              ...restButtonProps,
              disabled,
              // @ts-expect-error - TS2322 - Type '"small" | "medium"' is not assignable to type 'number | undefined'.
              size,
              ref: refs.setReference,
              onClick: (e) => {
                // @ts-expect-error - TS2345 - Argument of type 'MouseEvent<Element, MouseEvent>' is not assignable to parameter of type 'SyntheticEvent<HTMLElement, Event>'.
                onClick?.(e);
                e.stopPropagation();
              },
              className: classify(
                css.button,
                {[css.isFluid]: isFluid === true},
                classNames?.wrapper,
              ),
              tabIndex,
            })}
          >
            {children}
          </UnstyledButton>
        </ConditionalWrapper>

        {isMenuOpen && menu && (
          <div
            {...getFloatingProps({
              ref: refs.setFloating,
              style: {
                position: strategy,
                top: y ?? spaceNone,
                left: x ?? spaceNone,
                display: 'flex',
              },
            })}
          >
            <Menu
              {...menu}
              onSelect={(option, e) => {
                onOptionSelect?.(option, e);
                if (
                  (!menu.optionsVariant || menu.optionsVariant === 'normal') &&
                  option.key !== 'language_support'
                ) {
                  setIsMenuOpen(false);
                }
              }}
              size={menu.size || size}
              onTabOut={() => setIsMenuOpen(false)}
            />
          </div>
        )}
      </div>
    );
  },
);
